import React from 'react';

import { closestTo, format, parse } from 'date-fns';
import { DAYPART_FORMAT, TWELVE_HOUR_TIME_PARSE_FORMAT } from 'features/store/constants';
import { capitalize } from 'lodash-es';
import { StyleSheet } from 'react-native';

import Picture from 'components/picture';
import { Box, Typography } from 'components/ui';
import type { BoxProps } from 'components/ui';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { IValidDayPart } from 'state/menu/main-menu/types';
import { layout, spacing } from 'styles/constants';

type ItemUnavailableDayPart = Omit<IValidDayPart, 'displayName' | 'icon'> & {
  displayName?: IValidDayPart['displayName'];
  icon?: IValidDayPart['icon'];
};

interface IProps {
  dayParts: ReadonlyArray<ItemUnavailableDayPart>;
  itemDayParts: string[];
  marginAndBorders: boolean;
}

const styles = StyleSheet.create({
  contentWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    maxWidth: spacing.$64,
    width: layout.full,
  },
  contentWrapperDesktop: {
    maxWidth: spacing.$72,
  },
  contentWithIcon: {
    flex: 0.7,
  },
  fullFlex: {
    flex: 1,
  },
  iconFlex: {
    flex: 0.3,
  },
  textWrapper: {
    flexDirection: 'column',
    justifyContent: 'center',
  },
});

const ContentWrapper = ({ style, ...props }: BoxProps) => {
  const isDesktop = useIsDesktopBreakpoint();

  return React.createElement(Box, {
    ...props,
    style: [styles.contentWrapper, isDesktop && styles.contentWrapperDesktop, style],
  });
};

const TextWrapper = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.textWrapper, style] });

const ItemUnavailableMessage = ({ dayParts, itemDayParts, marginAndBorders }: IProps) => {
  const now = new Date();
  const itemDayPartObjects = dayParts.filter(part => itemDayParts.includes(part.key));
  const itemDayPartBoundaryDateObjects = itemDayPartObjects.flatMap(part => [
    parse(part.startTime, DAYPART_FORMAT, now),
    parse(part.endTime, DAYPART_FORMAT, now),
  ]);
  const closestDayPartBoundaryDateObj = closestTo(now, itemDayPartBoundaryDateObjects);

  if (!closestDayPartBoundaryDateObj) {
    return null;
  }

  const closestDayPartBoundaryString = format(closestDayPartBoundaryDateObj, DAYPART_FORMAT);
  const closestItemDayPartObject = itemDayPartObjects.find(part =>
    Object.values(part).some(time => time === closestDayPartBoundaryString)
  );

  if (!closestItemDayPartObject) {
    return null;
  }

  const { startTime, endTime, displayName, icon } = closestItemDayPartObject;
  const dayPartName = displayName?.locale?.trim();

  if (!dayPartName) {
    // Operational day-part schedules may not include localized presentation metadata.
    return (
      <ContentWrapper>
        <Box style={styles.fullFlex}>
          <TextWrapper>
            <Typography variant="headerThree" m="$0">
              {t('incentiveFeedbackOutOfDayPart')}
            </Typography>
          </TextWrapper>
        </Box>
      </ContentWrapper>
    );
  }

  let dayPartMessage = '';
  let timeBoundaryMessage = '';
  const nowInDayPartFormat = format(now, DAYPART_FORMAT);

  if (nowInDayPartFormat < startTime) {
    dayPartMessage = t('daypartBeforeStartTime', { dayPart: dayPartName.toLowerCase() });
    timeBoundaryMessage = t('daypartStartsAt', {
      dayPart: capitalize(dayPartName),
      startTime: format(parse(startTime, DAYPART_FORMAT, now), TWELVE_HOUR_TIME_PARSE_FORMAT),
    });
  } else {
    dayPartMessage = t('daypartAfterEndTime', { dayPart: dayPartName.toLowerCase() });
    timeBoundaryMessage = t('daypartEndsAt', {
      dayPart: capitalize(dayPartName),
      endTime: format(parse(endTime, DAYPART_FORMAT, now), TWELVE_HOUR_TIME_PARSE_FORMAT),
    });
  }

  return (
    <ContentWrapper>
      {icon && (
        <Box style={styles.iconFlex}>
          <Picture image={icon} alt={dayPartName} width={'40px'} />
        </Box>
      )}
      <Box style={icon ? styles.contentWithIcon : styles.fullFlex}>
        <TextWrapper>
          {dayPartMessage && (
            <Typography variant="headerThree" m="$0">
              {dayPartMessage}
            </Typography>
          )}
          {timeBoundaryMessage && <Typography variant="copyTwo">{timeBoundaryMessage}</Typography>}
        </TextWrapper>
      </Box>
    </ContentWrapper>
  );
};

export default ItemUnavailableMessage;
